import React, { useMemo } from "react";
import CardTable from "../../components/common/CardTable";
import Table, {
  TableColumn,
  TableRow,
} from "../../components/common/Table/Table";
import Pagination from "../../components/common/Table/Pagination";
import { useNavigate } from "react-router-dom";

const ITEMS_PER_PAGE = 10;

const columns: TableColumn[] = [
  { key: "lastName", label: "Last name", isSortable: true },
  { key: "firstName", label: "First name", isSortable: true },
  { key: "nuid", label: "NUID" },
  { key: "location", label: "Location" },
  { key: "add", label: "Add" },
];

interface User {
  lastName: string;
  firstName: string;
  nuid: string;
  location: string;
}

interface SearchUserTableProps {
  onAdd?: (user: User) => void;
  users?: User[];
  totalItems?: number;
  onSortChange?: (sortBy: string, sortOrder: string) => void;
  sortColumn?: string;
  sortDirection?: "asc" | "desc";
  currentPage?: number;
  setCurrentPage?: (page: number) => void;
  activeNuid?: string;
  activeMode?: "add" | "edit";
}

const SearchUserTable: React.FC<SearchUserTableProps> = ({
  onAdd,
  users = [],
  totalItems,
  onSortChange,
  sortColumn: propSortColumn,
  sortDirection: propSortDirection,
  currentPage = 1,
  setCurrentPage,
  activeNuid,
  activeMode,
}) => {
  const sortColumn = propSortColumn ?? "lastName";
  const sortDirection = propSortDirection ?? "asc";
  // Data is server-paginated — users is already the current page
  const paginatedUsers = useMemo(() => users, [users]);

  const handlePageChange = (page: number) => {
    if (setCurrentPage) setCurrentPage(page);
  };

  const handleAddClick = (user: User) => {
    if (onAdd) onAdd(user);
  };
  const navigate = useNavigate();
  function handleCancel(
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ): void {
    event.preventDefault();
    navigate(-1);
  }

  // API call on sort change
  const handleSort = (columnKey: string, direction: "ASC" | "DESC") => {
    const normalized: "asc" | "desc" = direction === "ASC" ? "asc" : "desc";
    if (onSortChange) onSortChange(columnKey, normalized);
  };

  const formatLocation = (loc?: string): string => {
    if (!loc) return "";
    // Use split/join to avoid super-linear backtracking from /\s*-\s*/g
    return loc.split("-").map((part) => part.trim()).filter(Boolean).join(" ");
  };

  const rows: TableRow[] = paginatedUsers.map((user) => {
    const isActive = activeNuid === user.nuid;
    const isActiveAdd = isActive && activeMode !== "edit";
    const addButtonClass = isActiveAdd
      ? "button flex items-center group bg-[#003B71]! text-white! hover:bg-[#003B71]! hover:text-white! focus:bg-[#003B71]! focus:text-white! [&>img]:brightness-0! [&>img]:invert!"
      : "button flex items-center group text-[#003B71]! hover:bg-[#003B71]! hover:text-white! focus:bg-[#003B71]! focus:text-white!";
    return {
      lastName: user.lastName,
      firstName: user.firstName,
      nuid: user.nuid,
      location: formatLocation(user.location),
      add: (
        <button
          type="button"
          className={addButtonClass}
          onClick={() => handleAddClick(user)}
        >
          <img
            src="/assets/images/plus-circle-outline.svg"
            alt="add"
            className="w-6 h-6 group-focus:brightness-0! group-focus:invert! group-hover:brightness-0! group-hover:invert!"
          />
          <span>{isActiveAdd ? "Adding" : "Add"}</span>
        </button>
      ),

      id: user.nuid,
    };
  });

  return (
    <div>
      <div className="lg:mx-5!">
        <CardTable>
          {rows.length > 0 ? (
            <Table
              columns={columns}
              rows={rows}
              onSort={handleSort}
              sortColumn={sortColumn}
              sortDirection={sortDirection.toUpperCase() as "ASC" | "DESC"}
            />
          ) : (
            <p className="p3 -book text-center mt-2.5! desktop:mt-50!">
              No users found.
            </p>
          )}
          <div className="mt-4! left-0 mobile:relative mobile:left-[30px]">
            <Pagination
              totalItems={totalItems ?? users.length}
              itemsPerPage={ITEMS_PER_PAGE}
              currentPage={currentPage}
              onPageChange={handlePageChange}
            />
          </div>
        </CardTable>
        <button
          type="button"
          className={`button flex items-center group hover:text-white! focus:text-white!`}
          onClick={(event) => {
            handleCancel(event);
          }}
        >
          <span>Back to Manage User Access</span>
        </button>
      </div>
    </div>
  );
};

export default SearchUserTable;
